import { instantToEpochMs } from './calendar.js';
const SECOND = 1000;
/**
 * How often a tablet says it is alive, and how often a running session reports.
 *
 * Both sides read these: the tablet to schedule its beats, the backend to decide
 * when silence means gone. Kept together because the second is only meaningful
 * as a multiple of the first.
 */
export const DEVICE_HEARTBEAT_INTERVAL_MS = 30 * SECOND;
export const SESSION_REPORT_INTERVAL_MS = 30 * SECOND;
/**
 * Silence longer than this and a tablet is offline.
 *
 * Three beats: an ordinary dropped request must not make a parent's dashboard
 * flicker, and a pause sent to a tablet that is really gone must not be stored
 * as though it will land.
 */
export const DEVICE_ONLINE_WINDOW_MS = 3 * DEVICE_HEARTBEAT_INTERVAL_MS;
/**
 * Silence longer than this and a session is over, whatever it last said.
 *
 * Two and a half reports. A tablet that is closed, suspended or out of battery
 * never ends its session; without this one, the dashboard said "watching for
 * three hours" over a tablet asleep in a drawer.
 */
export const SESSION_SILENCE_MS = (5 * SESSION_REPORT_INTERVAL_MS) / 2;
const ageMs = (since, at) => instantToEpochMs(at) - instantToEpochMs(since);
export const isDeviceOnline = (input) => input.lastSeenAt !== null && ageMs(input.lastSeenAt, input.at) < DEVICE_ONLINE_WINDOW_MS;
export const isSessionSilent = (input) => ageMs(input.lastReportedAt, input.at) > SESSION_SILENCE_MS;
/**
 * What a parent is told the tablet is doing.
 *
 * The one place "watching" is defined. It used to be "a session row is open",
 * which stayed true for a tablet that had been closed mid-episode — the session
 * was never ended because the app that would have ended it was gone.
 */
export const derivePresence = ({ isOnline, activity, hasLiveSession, }) => {
    if (!isOnline) {
        // Silence is told apart by what came before it: a tablet that announced it
        // was leaving the screen was closed, one that just stopped lost its link.
        return activity?.appState === 'BACKGROUND' ? 'APP_CLOSED' : 'OFFLINE';
    }
    if (activity === null) {
        // An older build says nothing about itself; an open session is the best
        // evidence there is.
        return hasLiveSession ? 'WATCHING' : 'BROWSING';
    }
    if (activity.appState === 'BACKGROUND') {
        return 'BACKGROUND';
    }
    switch (activity.screen) {
        case 'PLAYER':
            return activity.isPlaying ? 'WATCHING' : 'VIDEO_PAUSED';
        case 'GAME':
            // A game the gate refused is a message on the screen, not a game.
            return activity.isPlaying ? 'PLAYING_GAME' : 'BROWSING';
        default:
            return 'BROWSING';
    }
};
//# sourceMappingURL=presence.js.map